import { fflStripeCustomerIdSchema } from '@/model/yupSchema.mjs';
import { Button, IconButton, Input, Text, useDisclosure, Spinner } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { keyType } from '@/queue/types';
import { CheckIcon, MinusIcon } from '@chakra-ui/icons';
import ConfirmationModal from '@/common/ConfirmationModal';
import { getStripeCustomerId, updateStripeCustomerId } from '../../api/apis';
import { ToasterNotification } from '@/toaster/toasterNotification';

function FFLStripeConfiguration() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [toggle, setToggle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [customerData, setCustomerData] = useState<{ customer_id?: string; is_active?: boolean } | null>(null);
  const [originalCustomerId, setOriginalCustomerId] = useState<string>('');

  const formMapping = [
    {
      label: 'Customer ID',
      inputType: 'text',
      fieldName: 'customerId'
    }
  ];

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(fflStripeCustomerIdSchema),
    mode: 'onChange',
    defaultValues: {
      customerId: ''
    }
  });

  enum FormFields {
    customerId = 'customerId'
  }

  // Fetch customer data on component mount
  const fetchCustomerData = async () => {
    setIsLoading(true);
    try {
      const response = await getStripeCustomerId();
      if (response?.status === 200 && response?.data) {
        const data = response.data;
        setCustomerData(data);
        setToggle(data.is_active || false);
        const customerId = data.customer_id || '';
        setValue('customerId', customerId);
        setOriginalCustomerId(customerId);
      } else {
        // No data found
        setCustomerData(null);
        setToggle(false);
        setValue('customerId', '');
        setOriginalCustomerId('');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setCustomerData(null);
      setToggle(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, []);

  // Handle toggle disable (when user clicks on "Toggle Enabled" button and confirms)
  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const payload = {
        is_active: false
      };
      const response = await updateStripeCustomerId(payload);
      if (response?.status === 200) {
        setToggle(false);
        setCustomerData({ ...customerData, is_active: false });
        ToasterNotification('success', 'Stripe Customer ID integration disabled successfully');
        onClose();
      }
    } catch (error) {
      console.error('Toggle error:', error);
      ToasterNotification('error', 'Failed to disable Stripe Customer ID integration');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle enable toggle (when user clicks on "Toggle Disabled" button)
  const handleEnableToggle = async () => {
    try {
      setIsLoading(true);
      const payload = {
        is_active: true
      };
      const response = await updateStripeCustomerId(payload);
      if (response?.status === 200) {
        setToggle(true);
        setCustomerData({ ...customerData, is_active: true });
        ToasterNotification('success', 'Stripe Customer ID integration enabled successfully');
        onClose();
      }
    } catch (error) {
      console.error('Enable toggle error:', error);
      ToasterNotification('error', 'Failed to enable Stripe Customer ID integration');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle update (when user clicks "Update" button)
  const updateHandler: SubmitHandler<{
    customerId?: string;
  }> = async (data) => {
    setIsUpdating(true);
    try {
      const payload = {
        customer_id: data.customerId || "",
        is_active: true
      };
      const response = await updateStripeCustomerId(payload);
      if (response?.status === 200) {
        setCustomerData(response.data);
        setToggle(true);
        const newCustomerId = data.customerId || '';
        setOriginalCustomerId(newCustomerId);
        ToasterNotification('success', 'Stripe Customer ID updated successfully');
      }
    } catch (error) {
      console.error('Update error:', error);
      ToasterNotification('error', 'Failed to update Stripe Customer ID');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cancel (restore to original value)
  const cancelHandler = () => {
    setValue('customerId', originalCustomerId);
  };

  // Watch for changes in the input field
  const currentCustomerId = watch('customerId');
  const hasChanges = toggle && currentCustomerId !== originalCustomerId;

  return (
    <>
      <div className="ffl-select-admin-tab stripe-toggle-wrapper">
        <Text fontSize="30px" as="b">
          Stripe Customer ID Integration
        </Text>
        {toggle ? (
          <Button
            leftIcon={
              <IconButton
                isRound={true}
                variant="solid"
                colorScheme="teal"
                aria-label="Done"
                size="xs"
                icon={<CheckIcon />}
              />
            }
            isDisabled={isLoading}
            size="lg"
            colorScheme="teal"
            variant="outline"
            className="btn-toggle stripe-toggle-btn"
            onClick={onOpen}
          >
            Toggle Enabled
          </Button>
        ) : (
          <Button
            leftIcon={
              isLoading ? (
                <Spinner size="sm" />
              ) : (
                <IconButton
                  isRound={true}
                  variant="solid"
                  colorScheme="red"
                  aria-label="Done"
                  size="xs"
                  icon={<MinusIcon />}
                />
                )
            }
            isDisabled={isLoading}
            size="lg"
            colorScheme="red"
            variant="outline"
            className="btn-toggle stripe-toggle-btn"
            onClick={handleEnableToggle}
          >
            Toggle Disabled
          </Button>
        )}
      </div>
      <form className="ffl-store-config-form">
        {formMapping.map((formItem) => {
          return (
            <>
              <div className="ffl-store-config-form__form-item stripe-input-wrapper">
                <label
                  className="ffl-store-config-form__label"
                  htmlFor={formItem.fieldName}
                >
                  {formItem.label}
                </label>
                <Input
                  className="ffl-store-config-form__input"
                  {...register(formItem.fieldName as FormFields)}
                  isDisabled={!toggle}
                />
              </div>
              {toggle && errors[formItem.fieldName as keyType<typeof errors>] &&
                errors[formItem.fieldName as keyType<typeof errors>]
                  ?.message && (
                  <div className="form-error">
                    {(errors[formItem.fieldName as keyType<typeof errors>]
                      ?.message as string) || ''}
                  </div>
                )}
            </>
          );
        })}
      </form>
      <div className="btn-wrappper">
        <Button
          colorScheme="red"
          background="#ffffff"
          color="#E53E3E"
          border="1px solid #E53E3E"
          padding="8px 11px"
          marginRight="20px"
          onClick={cancelHandler}
          isDisabled={!hasChanges}
        >
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          background="#0C1E46"
          padding="8px 20px"
          onClick={handleSubmit(updateHandler)}
          isLoading={isUpdating}
          isDisabled={!hasChanges}
        >
          Update
        </Button>
      </div>
      <ConfirmationModal
        isOpen={isOpen}
        modalHeader={'Disable Stripe Customer ID Integration'}
        onClose={onClose}
        isCancelBtnDisable={isLoading}
        isConfirmBtnDisable={isLoading}
        isConfirmBtnLoading={isLoading}
        onClickConfirmBtn={handleToggle}
        modalContent={
          <>
            This will disable your Stripe Customer ID integration. Do you want to proceed?
          </>
        }
      />
    </>
  );
}

export default FFLStripeConfiguration;
